<template>
  <LoadingContainer :is-loading="isLoading">
    <form class="has-sticky-submit" @submit.prevent="submit">
      <div class="card">
        <div class="card-header">
          <div class="card-header-title">
            <div class="title is-6 is-marginless">
              Product Lists
              <span class="tag">Επεξεργασία</span>
            </div>
          </div>

          <div class="card-header-icon">
            <div class="buttons is-marginless are-small">
              <RouterLink
                :to="{ name: 'content.home.product-lists.list' }"
                class="button"
              >
                <span class="icon is-small"><i class="fa fa-list"/></span>
                <span>Λίστα</span>
              </RouterLink>

              <RouterLink
                v-if="form.id"
                :to="{
                  name: 'content.home.product-lists.items',
                  params: { id: form.id },
                }"
                class="button is-link is-light"
              >
                <span class="icon is-small"><i class="fa fa-th-large"/></span>
                <span>Προϊόντα</span>
              </RouterLink>

              <button
                type="button"
                class="button is-danger is-outlined"
                @click="askToDelete"
              >
                <span class="icon is-small"><i class="fa fa-trash"/></span>
                <span>Διαγραφή</span>
              </button>
            </div>
          </div>
        </div>

        <div class="card-content">
          <!-- Row 1: Titles -->
          <div class="columns is-multiline">
            <div class="column is-half">
              <div class="field">
                <label class="label">Τίτλος (EL) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_el"
                    class="input"
                    type="text"
                    name="title_el"
                  />
                  <p v-show="errors.has('title_el')" class="help is-danger">
                    Εισάγετε ελληνικό τίτλο
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-half">
              <div class="field">
                <label class="label">Τίτλος (EN) *</label>
                <div class="control">
                  <input
                    v-validate="'required'"
                    v-model.trim="form.title_en"
                    class="input"
                    type="text"
                    name="title_en"
                  />
                  <p v-show="errors.has('title_en')" class="help is-danger">
                    Εισάγετε αγγλικό τίτλο
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Row 2: Order -->
          <div class="columns">
            <div class="column is-one-third">
              <div class="field">
                <label class="label">Σειρά εμφάνισης</label>
                <div class="control">
                  <input
                    v-model.number="form.order"
                    class="input"
                    type="number"
                    min="0"
                    step="1"
                    name="order"
                  />
                </div>
                <p class="help">
                  Χρησιμοποιείται στην ταξινόμηση λιστών (0, 1, 2, ...).
                </p>
              </div>
            </div>
          </div>

          <!-- Row 3: Active (full width on its own row) -->
          <div class="columns">
            <div class="column is-full">
              <div class="field">
                <label class="label">
                  Εμφανής *
                  <span class="tag is-rounded ml-2">Max 3 ενεργές</span>
                </label>
                <div class="control">
                  <ToggleButton
                    :sync="true"
                    :value="form.is_active"
                    :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                    :width="30"
                    :height="15"
                    @change="updateActive"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StickyFormFooter>
        <template #left>
          <FormSubmit :is-saving="isSaving" @reset="reset" />
        </template>
      </StickyFormFooter>
    </form>

    <Confirmation
      :model-name="form.title_el"
      :is-open="isDeleteOpen"
      description="Είστε βέβαιος για τη διαγραφή αυτής της λίστας;"
      @closeModal="closeDeleteModal"
    />
  </LoadingContainer>
</template>

<script>
import request from '@/utils/request';
import StickyFormFooter from '@/views/components/StickyFormFooter';
import LoadingContainer from '@/views/components/LoadingContainer';
import FormSubmit from '@/views/components/FormSubmit';
import Confirmation from '@/views/components/Confirmation';

export default {
  name: 'ProductListEdit',
  components: {
    LoadingContainer,
    StickyFormFooter,
    FormSubmit,
    Confirmation,
  },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDeleteOpen: false,
      original: null,
      form: {
        id: null,
        title_el: '',
        title_en: '',
        is_active: false,
        order: 0,
      },
    };
  },

  metaInfo: { title: 'Product Lists - Edit' },

  watch: {
    '$route.params.id': {
      immediate: true,
      async handler() {
        await this.fetchItem();
      },
    },
  },

  methods: {
    async fetchItem() {
      try {
        this.isLoading = true;
        const id = Number(this.$route.params.id);

        const { data } = await request.get('/home/product-lists');

        let list = [];
        if (Array.isArray(data)) list = data;
        else if (data && Array.isArray(data.data)) list = data.data;

        const found = list.find(x => Number(x.id) === id) || null;

        if (!found) {
          this.$notify({
            type: 'error',
            title: 'Σφάλμα',
            text: 'Η λίστα δεν βρέθηκε',
          });
          this.$router
            .push({ name: 'content.home.product-lists.list' })
            .catch(() => {});
          return;
        }

        this.original = found;
        this.form = {
          id: found.id,
          title_el: found.title_el || '',
          title_en: found.title_en || '',
          is_active: !!found.is_active,
          order: Number.isFinite(found.order) ? found.order : 0,
        };
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: (err && err.message) || 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    updateActive({ value }) {
      this.form.is_active = !!value;
    },

    async submit() {
      try {
        const ok = await this.$validator.validateAll();
        if (!ok) throw new Error('Client-side validation failed');

        this.isSaving = true;

        const payload = {
          title_el: this.form.title_el,
          title_en: this.form.title_en,
          is_active: this.form.is_active,
          order: Number.isFinite(this.form.order) ? this.form.order : 0,
        };

        const resp = await request.patch(
          `/home/product-lists/${this.form.id}`,
          payload,
        );

        if (resp.status !== 200) {
          throw new Error(resp?.data?.message || 'To αίτημα απέτυχε');
        }

        this.isSaving = false;

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η λίστα αποθηκεύτηκε',
        });

        await this.fetchItem();
      } catch (err) {
        this.isSaving = false;
        const msg =
          err?.response?.data?.message || err?.message || 'To αίτημα απέτυχε';
        const uiMsg =
          msg.includes('up to 3') || msg.includes('Maximum of 3')
            ? 'Μέγιστο 3 ενεργές λίστες'
            : msg;

        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: uiMsg,
        });
      }
    },

    askToDelete() {
      this.isDeleteOpen = true;
    },

    async closeDeleteModal(confirmed) {
      this.isDeleteOpen = false;
      if (!confirmed) return;

      try {
        const { data } = await request.delete(
          `/home/product-lists/${this.form.id}`,
        );
        if (data && data.error) {
          throw new Error(data.message || 'Server error');
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η λίστα διαγράφηκε',
        });

        this.$router
          .push({ name: 'content.home.product-lists.list' })
          .catch(() => {});
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: (err && err.message) || 'To αίτημα απέτυχε',
        });
      }
    },

    reset() {
      if (!this.original) return;
      this.form = {
        id: this.original.id,
        title_el: this.original.title_el || '',
        title_en: this.original.title_en || '',
        is_active: !!this.original.is_active,
        order: Number.isFinite(this.original.order) ? this.original.order : 0,
      };
      this.$validator.reset();
    },
  },
};
</script>
